/*
 * ErrorScreen — agent halted before verification (tool failure or network
 * error). Ported from ui-handoff/.../app.jsx::ErrorScreen, parameterised on
 * the real trace so the operator sees what actually fired before the failure.
 */

import React from 'react';

function lastErrorEvent(events) {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i].kind === 'error') return events[i];
  }
  return null;
}

export function ErrorScreen({ query, traceEvents, onRetry, onNewSearch }) {
  const errorEvent = lastErrorEvent(traceEvents);
  const completedToolCalls = traceEvents.filter(
    (e) => e.kind === 'tool_call' && (e.output || []).length > 0,
  );

  return (
    <div className="err fade-in">
      <div className="err-tag mono">
        <span className="err-dot" />
        agent · error
      </div>
      <h2 className="err-h serif">
        Tool call failed —<br />
        <span className="err-h-dim">the agent stopped before reaching verification.</span>
      </h2>
      <div className="err-trace mono">
        {completedToolCalls.map((event, i) => (
          <div key={i} className="err-trace-row">
            <span className="err-trace-tag err-trace-tag-ok">OK</span>
            {event.name} · {(event.output || []).length} lines
          </div>
        ))}
        {errorEvent && (
          <div className="err-trace-row err-trace-row-bad">
            <div>
              <span className="err-trace-tag err-trace-tag-bad">ERR</span>
              {errorEvent.tool || 'agent'} · {errorEvent.message}
            </div>
            {errorEvent.status_code != null && (
              <div className="err-trace-detail">
                status: <span className="err-em">{errorEvent.status_code}</span>
              </div>
            )}
          </div>
        )}
        {!errorEvent && (
          <div className="err-trace-row err-trace-row-bad">
            <div>
              <span className="err-trace-tag err-trace-tag-bad">ERR</span>
              stream closed unexpectedly
            </div>
          </div>
        )}
      </div>
      <p className="err-q">
        Query: <em>"{query}"</em>
      </p>
      <div className="err-actions">
        <button type="button" className="empty-btn-primary mono" onClick={onRetry}>
          retry
        </button>
        <button type="button" className="empty-btn-ghost mono" onClick={onNewSearch}>
          start over
        </button>
      </div>
      <style>{`
        .err { max-width: 720px; margin: 0 auto; padding: 80px 32px 64px; display: flex; flex-direction: column; gap: 16px; }
        .err-tag { display: inline-flex; align-items: center; gap: 8px; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: #c93a2a; align-self: flex-start; }
        .err-dot { width: 8px; height: 8px; border-radius: 99px; background: #c93a2a; box-shadow: 0 0 8px #c93a2a; }
        .err-h { font-size: 32px; line-height: 1.25; font-weight: 400; color: var(--ink); margin: 0; max-width: 600px; text-wrap: balance; }
        .err-h-dim { color: var(--ink-3); }
        .err-trace { background: var(--terminal-bg); color: var(--terminal-ink); padding: 16px 18px; font-size: 12px; line-height: 1.8; margin-top: 12px; }
        .err-trace-row { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
        .err-trace-row-bad { flex-direction: column; align-items: flex-start; gap: 4px; }
        .err-trace-tag { display: inline-block; font-size: 9.5px; letter-spacing: 0.16em; padding: 1px 6px; border: 1px solid currentColor; margin-right: 4px; }
        .err-trace-tag-ok { color: var(--terminal-pos); }
        .err-trace-tag-bad { color: var(--terminal-err); }
        .err-trace-detail { color: var(--terminal-dim); font-size: 11.5px; padding-left: 38px; }
        .err-em { color: var(--terminal-err); }
        .err-q { color: var(--ink-2); font-size: 15px; margin: 8px 0; font-family: var(--serif); }
        .err-q em { color: var(--accent); }
        .err-actions { display: flex; gap: 10px; margin-top: 8px; }
        .empty-btn-primary, .empty-btn-ghost { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; padding: 9px 18px; border: 1px solid var(--ink); }
        .empty-btn-primary { background: var(--ink); color: var(--bg); }
        .empty-btn-primary:hover { background: var(--accent); border-color: var(--accent); }
        .empty-btn-ghost { background: transparent; color: var(--ink); border-color: var(--rule-2); }
        .empty-btn-ghost:hover { border-color: var(--ink); }
      `}</style>
    </div>
  );
}
